<script>
// Compact customer picker for the Property Appointments filter row (PRD §6:
// "filters for project, type, agent, customer and date"). Searches the same
// Chatwoot contacts endpoint as components/dahreply/SelectCustomer.vue, but
// that component carries its own label/margins and has no way to clear a
// pick, so it doesn't fit a filter bar. v-model is the contact
// ({ id, name }) or null.
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';
import { FIELD_CLASS } from '../constants';

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'CustomerFilter',
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      query: this.value ? this.value.name : '',
      results: [],
      isOpen: false,
      isSearching: false,
      debounceTimer: null,
      // Set when this component itself drops the pick because the user
      // started typing over it — that `null` must not wipe what they typed.
      isClearingForTyping: false,
      // Bumped per search: focusing the empty box starts a broad search, and
      // typing starts a narrower one right after — whichever returns last
      // must not win unless it's the latest.
      searchSeq: 0,
    };
  },
  watch: {
    // Syncs the box when the parent changes the pick (e.g. Clear filters).
    value(next) {
      if (this.isClearingForTyping) {
        this.isClearingForTyping = false;
        return;
      }
      this.query = next ? next.name : '';
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    handleInput(event) {
      this.query = event.target.value;
      this.isOpen = true;
      // Typing over a picked customer drops the filter until a new one is
      // chosen — the list shouldn't stay filtered to a name no longer shown.
      if (this.value) {
        this.isClearingForTyping = true;
        this.$emit('input', null);
      }
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(this.search, SEARCH_DEBOUNCE_MS);
    },
    handleFocus() {
      this.isOpen = true;
      if (!this.results.length) this.search();
    },
    closeWithDelay() {
      // Lets a click on a result land before the list disappears.
      setTimeout(() => {
        this.isOpen = false;
      }, 150);
    },
    async search() {
      this.searchSeq += 1;
      const seq = this.searchSeq;
      this.isSearching = true;
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const url = new URL(
          `${window.location.origin}/api/v1/accounts/${accountId}/appointments/contacts`
        );
        url.searchParams.append('page', 1);
        url.searchParams.append('q', this.query.trim());
        const response = await fetch(url.toString(), {
          headers: { ...getCentralHeaders() },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json();
        if (seq !== this.searchSeq) return;
        this.results = body.contacts || [];
      } catch (err) {
        if (seq !== this.searchSeq) return;
        this.results = [];
        // eslint-disable-next-line no-console
        console.error('Failed to search contacts:', err);
      } finally {
        if (seq === this.searchSeq) this.isSearching = false;
      }
    },
    select(contact) {
      this.$emit('input', { id: contact.id, name: contact.name });
      this.isOpen = false;
    },
    clear() {
      this.query = '';
      this.results = [];
      this.$emit('input', null);
    },
  },
};
</script>

<template>
  <div class="relative w-full max-w-[220px]">
    <input
      :value="query"
      type="text"
      :placeholder="$t('REAL_ESTATE.APPOINTMENTS.FILTER_CUSTOMER_PLACEHOLDER')"
      class="reset-base mb-0 pr-8"
      :class="fieldClass"
      @input="handleInput"
      @focus="handleFocus"
      @blur="closeWithDelay"
    />
    <button
      v-if="query"
      type="button"
      class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
      :aria-label="$t('REAL_ESTATE.APPOINTMENTS.FILTER_CUSTOMER_CLEAR')"
      @click="clear"
    >
      <fluent-icon icon="dismiss" size="14" />
    </button>
    <ul
      v-if="isOpen"
      class="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg"
    >
      <li
        v-if="!results.length"
        class="px-3 py-2 text-sm text-slate-500 dark:text-slate-400"
      >
        {{
          isSearching
            ? $t('REAL_ESTATE.APPOINTMENTS.FILTER_CUSTOMER_SEARCHING')
            : $t('REAL_ESTATE.APPOINTMENTS.FILTER_CUSTOMER_NONE')
        }}
      </li>
      <li
        v-for="contact in results"
        :key="contact.id"
        class="px-3 py-2 text-sm cursor-pointer text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-600"
        @mousedown.prevent="select(contact)"
      >
        {{ contact.name }}
        <span
          v-if="contact.phone_number"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          ({{ contact.phone_number }})
        </span>
      </li>
    </ul>
  </div>
</template>
